import React from "react";
import { Routes, Route, Navigate, useParams, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { sharedApi } from "@/lib/api";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFAB from "@/components/layout/WhatsAppFAB";
import GoogleSessionRedeemer from "@/components/auth/GoogleSessionRedeemer";
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import CategoryPage from "@/pages/CategoryPage";
import AdDetailPage from "@/pages/AdDetailPage";
import PostAdPage from "@/pages/PostAdPage";
import SellPickerPage from "@/pages/SellPickerPage";
import MyAdsPage from "@/pages/MyAdsPage";
import MyAdsFeedbackPage from "@/pages/MyAdsFeedbackPage";
import AccountPage from "@/pages/AccountPage";
import AccountEditPage from "@/pages/AccountEditPage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import BuyPointsPage from "@/pages/BuyPointsPage";
import AdminPointsPurchasesPage from "@/pages/AdminPointsPurchasesPage";
import PointsHistoryPage from "@/pages/PointsHistoryPage";
import SavedSearchesPage from "@/pages/SavedSearchesPage";
import MyVisitsPage from "@/pages/MyVisitsPage";
import GoogleDonePage from "@/pages/GoogleDonePage";
import MyVouchersPage from "@/pages/MyVouchersPage";
import RedeemVoucherPage from "@/pages/RedeemVoucherPage";
import SellerPage from "@/pages/SellerPage";
import ChatPage from "@/pages/ChatPage";
import LoginPage from "@/pages/LoginPage";
import PhoneLoginPage from "@/pages/PhoneLoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PrerenderStatus, { isCrawler } from "@/components/common/PrerenderStatus";
import PlansPage from "@/pages/PlansPage";
import MySubscriptionPage from "@/pages/MySubscriptionPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import BlogListPage from "@/pages/BlogListPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FAQPage from "@/pages/FAQPage";
import PagePage from "@/pages/PagePage";
import ContactPage from "@/pages/ContactPage";
import FeedbackPage from "@/pages/FeedbackPage";
import DealsPage from "@/pages/DealsPage";
import DealDetailPage from "@/pages/DealDetailPage";
import PostDealPage from "@/pages/PostDealPage";
import MyDealsPage from "@/pages/MyDealsPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import PostEventPage from "@/pages/PostEventPage";
import MyEventsPage from "@/pages/MyEventsPage";
import ListYourBusinessPage from "@/pages/ListYourBusinessPage";
import MyAlertsPage from "@/pages/MyAlertsPage";
import MyViewingRequestsPage from "@/pages/MyViewingRequestsPage";
import BoostReturnPage from "@/pages/BoostReturnPage";
import CarValuationPage from "@/pages/CarValuationPage";
import CarValuationSuccessPage from "@/pages/CarValuationSuccessPage";
import MyFavoritesPage from "@/pages/MyFavoritesPage";
import JobProfilePage from "@/pages/jobs/JobProfilePage";
import JobDocumentsPage from "@/pages/jobs/JobDocumentsPage";
import JobApplyPage from "@/pages/jobs/JobApplyPage";
import JobApplicationsPage from "@/pages/jobs/JobApplicationsPage";
import JobApplicationDetailPage from "@/pages/jobs/JobApplicationDetailPage";
import JobEmployerPage from "@/pages/jobs/JobEmployerPage";
import JobSavedPage from "@/pages/jobs/JobSavedPage";
import RewardsHubPage from "@/pages/RewardsHubPage";
import RewardsTermsPage from "@/pages/RewardsTermsPage";
import PartnerProgramPage from "@/pages/PartnerProgramPage";
import EngagePage from "@/pages/EngagePage";
import EngageResumePage from "@/pages/EngageResumePage";
import LegalPolicyPage from "@/pages/LegalPolicyPage";
import TillPage from "@/pages/TillPage";
import AddPhonePage from "@/pages/AddPhonePage";
import { RequireAuth, useAuth } from "@/lib/auth";
import { AnalyticsPageViews } from "@/lib/ga";
import PendingReviewRibbon from "@/components/engage/PendingReviewRibbon";
import BackToSessionRibbon from "@/components/engage/BackToSessionRibbon";

/** Mobile fix — reset scroll on every PUSH/REPLACE navigation so tapping a
 *  listing mid-scroll never opens the next page at the footer. POP (browser
 *  back/forward) is left alone so the browser can restore scroll position. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  React.useEffect(() => {
    if (navType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

/** Iter 755 — post-login phone gate. Accounts that signed in without a
 *  mobile on file (`needs_phone` from /auth/me, hydrated by Google SSO
 *  and login flows) are routed to the Add phone screen before anything
 *  else. The 428 interceptor in lib/api.js is the write-action backstop. */
function PhoneGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!user || user.needs_phone !== true) return;
    const p = location.pathname;
    const exempt =
      p.startsWith("/account/add-phone") || p.startsWith("/login") ||
      p.startsWith("/register") || p.startsWith("/auth/") ||
      p.startsWith("/engage/") || p.startsWith("/engage-test") || p === "/till";
    if (!exempt) {
      navigate(`/account/add-phone?next=${encodeURIComponent(p + location.search)}`, { replace: true });
    }
  }, [user, location.pathname, location.search, navigate]);
  return null;
}

export default function App() {
  const location = useLocation();

  // Iter 697 §1 / WO 806 — only the public production hosts may be
  // indexed; every other host (staging, previews, localhost) gets a
  // noindex meta. (Static public/robots.txt can't vary per host.)
  React.useEffect(() => {
    const h = window.location.hostname;
    const isProd = /^(www\.)?(allsale\.co\.nz|demoallsale\.com)$/.test(h);
    if (!isProd || h === "staging.demoallsale.com") {
      if (document.head.querySelector("meta[data-allsale-host-robots]")) return;
      const m = document.createElement("meta");
      m.name = "robots";
      m.content = "noindex, nofollow";
      m.setAttribute("data-allsale-host-robots", "1");
      document.head.appendChild(m);
    }
  }, []);

  // Iter-288 — white-label /engage/{qr} page must look like the store's
  // own app (no AllSale header/nav/FAB). Chrome hidden on this route
  // only; every other route keeps its full shell.
  // Iter-325 — /till (staff till on store devices) is also chromeless.
  const isWhiteLabel =
    location.pathname.startsWith("/engage/") || location.pathname === "/till";
  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <ScrollToTop />
      <CrawlerRouteStatus />
      <PhoneGate />
      <AnalyticsPageViews />
      <GoogleSessionRedeemer />
      {!isWhiteLabel && <Header />}
      {!isWhiteLabel && <PendingReviewRibbon />}
      {!isWhiteLabel && <BackToSessionRibbon />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/phone" element={<PhoneLoginPage />} />
          {/* Iter 720 — password reset (email link opens /reset-password) */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* WO 800 §1 — e-mail verification links land here */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />
          <Route path="/auth/google/done" element={<GoogleDonePage />} />
          <Route path="/sell" element={<RequireAuth><SellPickerPage /></RequireAuth>} />
          <Route path="/post-ad" element={<RequireAuth><PostAdPage /></RequireAuth>} />
          <Route path="/edit-ad/:id" element={<RequireAuth><PostAdPage /></RequireAuth>} />
          <Route path="/my-ads" element={<RequireAuth><MyAdsPage /></RequireAuth>} />
          <Route path="/my-ads/feedback" element={<RequireAuth><MyAdsFeedbackPage /></RequireAuth>} />
          <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
          <Route path="/account/add-phone" element={<RequireAuth><AddPhonePage /></RequireAuth>} />
          <Route path="/account/edit" element={<RequireAuth><AccountEditPage /></RequireAuth>} />
          <Route path="/account/payment-methods" element={<RequireAuth><PaymentMethodsPage /></RequireAuth>} />
          <Route path="/account/saved-searches" element={<RequireAuth><SavedSearchesPage /></RequireAuth>} />
          <Route path="/my/vouchers" element={<RequireAuth><MyVouchersPage /></RequireAuth>} />
          <Route path="/my/visits" element={<RequireAuth><MyVisitsPage /></RequireAuth>} />
          <Route path="/redeem/:voucherId" element={<RedeemVoucherPage />} />
          <Route path="/seller/:id" element={<SellerPage />} />
          <Route path="/chat" element={<RequireAuth><ChatPage /></RequireAuth>} />
          <Route path="/chat/:chatId" element={<RequireAuth><ChatPage /></RequireAuth>} />
          <Route path="/plan-and-pricing" element={<PlansPage />} />
          {/* Iter-274e W1: header still links to /pricing — keep the
              canonical route at /plan-and-pricing and redirect the short
              form so the tab never 404s. */}
          <Route path="/pricing" element={<Navigate to="/plan-and-pricing" replace />} />
          <Route path="/my-subscription" element={<RequireAuth><MySubscriptionPage /></RequireAuth>} />
          <Route path="/my/alerts" element={<RequireAuth><MyAlertsPage /></RequireAuth>} />
          <Route path="/my/viewing-requests" element={<RequireAuth><MyViewingRequestsPage /></RequireAuth>} />
          <Route path="/boost/return" element={<RequireAuth><BoostReturnPage /></RequireAuth>} />
          <Route path="/car-valuation" element={<CarValuationPage />} />
          <Route path="/car-valuation/success" element={<CarValuationSuccessPage />} />
          <Route path="/my/favorites" element={<MyFavoritesPage />} />
          <Route path="/jobs/profile" element={<RequireAuth><JobProfilePage /></RequireAuth>} />
          <Route path="/jobs/documents" element={<RequireAuth><JobDocumentsPage /></RequireAuth>} />
          <Route path="/jobs/apply/:id" element={<RequireAuth><JobApplyPage /></RequireAuth>} />
          <Route path="/jobs/applications" element={<RequireAuth><JobApplicationsPage /></RequireAuth>} />
          <Route path="/jobs/applications/:id" element={<RequireAuth><JobApplicationDetailPage /></RequireAuth>} />
          <Route path="/jobs/employer" element={<RequireAuth><JobEmployerPage /></RequireAuth>} />
          <Route path="/jobs/saved" element={<RequireAuth><JobSavedPage /></RequireAuth>} />
          <Route path="/rewards" element={<RequireAuth><RewardsHubPage /></RequireAuth>} />
          <Route path="/points/buy" element={<RequireAuth><BuyPointsPage /></RequireAuth>} />
          <Route path="/points/history" element={<RequireAuth><PointsHistoryPage /></RequireAuth>} />
          <Route path="/admin/points-purchases" element={<RequireAuth><AdminPointsPurchasesPage /></RequireAuth>} />
          <Route path="/rewards-terms" element={<RewardsTermsPage />} />
          <Route path="/rewards/terms" element={<RewardsTermsPage />} />
          {/* Iter 784 — owner-managed footer legal pages */}
          <Route path="/legal/:slug" element={<LegalPolicyPage />} />
          <Route path="/partner-program" element={<PartnerProgramPage />} />
          <Route path="/partner-program/:token" element={<PartnerProgramPage />} />
          <Route path="/engage/:qrCodeId" element={<EngagePage />} />
          {/* Iter 775 — e-mail "Finish my review" links land here and are
              resolved to /engage/{qr}?resume={session_id}. */}
          <Route path="/engage-test/customer" element={<EngageResumePage />} />
          <Route path="/engage-test/customer/*" element={<EngageResumePage />} />
          <Route path="/till" element={<TillPage />} />
          <Route path="/list-your-business" element={<ListYourBusinessPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact-us" element={<ContactPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/deals/post" element={<RequireAuth><PostDealPage /></RequireAuth>} />
          <Route path="/my/deals" element={<RequireAuth><MyDealsPage /></RequireAuth>} />
          <Route path="/deals/:id" element={<DealDetailPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/post" element={<RequireAuth><PostEventPage /></RequireAuth>} />
          <Route path="/my/events" element={<RequireAuth><MyEventsPage /></RequireAuth>} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/p/:slug" element={<PagePage />} />
          {/* Iter-288e — bare /jobs hub redirects to the jobs category
              listing (all live job classifieds). Individual /jobs/*
              screens keep their own routes below. */}
          <Route path="/jobs" element={<Navigate to="/c/jobs" replace />} />
          {/* Legacy CMS URLs — redirect the two content paths the shared
              backend saw 404s for on prod. */}
          <Route path="/shipping" element={<Navigate to="/p/shipping" replace />} />
          <Route path="/safe-buying" element={<Navigate to="/p/safe-buying" replace />} />
          <Route path="/help/:slug" element={<LegacyHelpRedirect />} />
          {/* Iter 667 — domain-flip 301 map targets. The generated legacy
              map (775 rules) points at /classified/{id}, /category/{slug}
              and /classified-events/{id}; alias them onto our real routes
              so every rule lands without regenerating the map. */}
          <Route path="/classified/:id" element={<ClassifiedAlias />} />
          <Route path="/category/:slug" element={<LegacyPathResolver fallback="category" />} />
          <Route path="/category/:parent/:slug" element={<LegacyPathResolver />} />
          <Route path="/classified-events/:id" element={<EventAlias />} />
          {/* Iter 667 §1 — edge fallback in the SPA: old-site /listing/…
              URLs that bypass the Cloudflare bulk list resolve through
              GET /api/legacy-redirect (exact ad, else its category, else /). */}
          <Route path="/listing/*" element={<LegacyPathResolver />} />
          {/* Iter 701 Addendum E — legacy CMS /page/* URLs resolve through
              the same backend map (e.g. /page/complaints → /p/complaints). */}
          <Route path="/page/*" element={<LegacyPathResolver />} />
          {/* Iter 707 — the founder's legacy /admin bookmark must land on
              the Admin Pod panel, not the public site. Explicit routes so
              we never rely on the 404 → resolver path. */}
          <Route path="/admin" element={<AdminPanelRedirect />} />
          <Route path="/admin/*" element={<AdminPanelRedirect />} />
          <Route path="/admin_panel" element={<AdminPanelRedirect />} />
          <Route path="/admin_panel/*" element={<AdminPanelRedirect />} />
          <Route path="/wp-admin/*" element={<AdminPanelRedirect />} />
          <Route path="/c/:categorySlug" element={<CategoryPage />} />
          <Route path="/ad/:adId" element={<AdDetailPage />} />
          {/* Clean SEO URL — /:categorySlug/:title-slug-ad_xxx */}
          <Route path="/:categorySlug/:adSlug" element={<AdDetailPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          {/* WO 800 §4 — unknown paths check the 324-row legacy 301 map
              first (client-side hop for users; crawlers get a real 301
              from /api/seo/render). No match → the 404 page renders IN
              PLACE (noindex, no canonical) instead of bouncing to /404. */}
          <Route path="*" element={<CatchAllResolver />} />
        </Routes>
      </main>
      {!isWhiteLabel && <Footer />}
      {!isWhiteLabel && <WhatsAppFAB />}
    </div>
  );
}

// Addendum 4 (P0) — for crawler/headless UAs, the authoritative route
// resolver decides the real status of EVERY URL (incl. trailing-slash 301s
// and /listing/{id} → /ad/{id}); the snapshot carries it as prerender meta.
function CrawlerRouteStatus() {
  const { pathname, search } = useLocation();
  const [res, setRes] = React.useState(null);
  React.useEffect(() => {
    if (!isCrawler()) return undefined;
    let alive = true;
    setRes(null);
    sharedApi
      .get("/classifieds/seo/route", {
        params: { path: pathname, query: search.replace(/^\?/, "") },
      })
      .then(({ data }) => {
        if (alive && data && typeof data.status === "number") setRes(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [pathname, search]);
  if (!res || res.status === 200) return null;
  const loc = res.location
    ? res.location.startsWith("http")
      ? res.location
      : `https://allsale.co.nz${res.location}`
    : undefined;
  return <PrerenderStatus code={res.status} location={loc} />;
}

// WO 800 §4 — module-level cache of the legacy 301 map (10-min refresh,
// last good copy kept on error).
let _seoRedirects = { ts: 0, map: null };
async function getSeoRedirects() {
  if (_seoRedirects.map && Date.now() - _seoRedirects.ts < 10 * 60 * 1000) {
    return _seoRedirects.map;
  }
  try {
    const { data } = await sharedApi.get("/classifieds/seo/redirects");
    _seoRedirects = { ts: Date.now(), map: data?.redirects || {} };
  } catch {
    if (!_seoRedirects.map) _seoRedirects = { ts: Date.now(), map: {} };
    else _seoRedirects.ts = Date.now();
  }
  return _seoRedirects.map;
}

function CatchAllResolver() {
  const { pathname, search } = useLocation();
  const [resolved, setResolved] = React.useState(null); // null | {target} | {notFound:true}
  React.useEffect(() => {
    let alive = true;
    setResolved(null); // reset when the unknown path changes client-side
    getSeoRedirects().then((map) => {
      if (!alive) return;
      const key = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
      const target = map[key];
      setResolved(target ? { target: target + search } : { notFound: true });
    });
    return () => {
      alive = false;
    };
  }, [pathname, search]);
  if (!resolved) return null;
  if (resolved.target) {
    // Crawlers get a real 301 via prerender meta signalling (the renderer
    // maps it onto the HTTP response); humans get the instant client hop.
    if (isCrawler()) {
      return (
        <>
          <PrerenderStatus code={301} location={`https://allsale.co.nz${resolved.target}`} />
          <MovedNotice to={resolved.target} />
        </>
      );
    }
    return <Navigate to={resolved.target} replace />;
  }
  return <NotFoundPage />;
}

function MovedNotice({ to }) {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
      <p className="text-slate-600">
        This page has moved to{" "}
        <RouterLinkMoved to={to} />
      </p>
    </div>
  );
}

function RouterLinkMoved({ to }) {
  return (
    <a href={to} className="font-semibold text-[color:var(--brand-blue)] underline" data-testid="moved-notice-link">
      {`https://allsale.co.nz${to}`}
    </a>
  );
}

// Iter-286 — legacy `/help/:slug` URLs (old site had `/help/shipping`,
// `/help/safe-buying`) redirect to the CMS `/p/:slug` route so the
// shared backend serves them from `GET /classifieds/pages/{slug}`.
function LegacyHelpRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/p/${slug}`} replace />;
}

// Iter 707 — hard redirect for the legacy admin URLs. The real panel
// lives on the Admin Pod host; replace() keeps Back history clean.
function AdminPanelRedirect() {
  React.useEffect(() => {
    window.location.replace("https://nz.allsale.co.nz/classifieds-admin");
  }, []);
  return null;
}

// Iter 667/697 — aliases for the legacy 301 map's target path templates.
function ClassifiedAlias() {
  const { id } = useParams();
  return <Navigate to={`/ad/${id}`} replace />;
}
function EventAlias() {
  const { id } = useParams();
  return <Navigate to={`/events/${id}`} replace />;
}

// Iter 697 item 5 — legacy-map targets come back in the old templates;
// translate them onto our real routes before navigating.
const LEGACY_TARGET_MAP = [
  [/^\/classified\//, "/ad/"],
  [/^\/category\//, "/c/"],
  [/^\/classified-events\//, "/events/"],
];
const mapLegacyTarget = (t) => {
  for (const [re, rep] of LEGACY_TARGET_MAP) if (re.test(t)) return t.replace(re, rep);
  return t;
};

/**
 * Iter 697 item 5 — old-site paths (/listing/{parent}/{slug}-{n},
 * /category/{old-slug}, /category/{parent}/{slug}-{n}) were landing on the
 * homepage with a 200 — SEO loss. Resolve them through the backend's
 * 775-row map (GET /api/legacy-redirect) and land on the real page.
 * fallback="category" keeps the plain /category/{slug} → /c/{slug} hop
 * when the map has no row (slug unchanged between sites).
 */
function LegacyPathResolver({ fallback = "home" }) {
  const { pathname, search } = useLocation();
  const params = useParams();
  const nav = useNavigate();
  React.useEffect(() => {
    // Crawlers: CrawlerRouteStatus already emits the resolver-truth status
    // meta for this URL — no client hop, nothing to do here.
    if (isCrawler()) return undefined;
    let alive = true;
    const go = (target) => {
      if (alive) nav(target, { replace: true });
    };
    const fallbackPath = () =>
      fallback === "category" && params.slug ? `/c/${params.slug}` : "/";
    // Addendum 4 — the authoritative route resolver wins first; the fuzzy
    // legacy-redirect lookup only handles long-tail old-site URLs it misses.
    sharedApi
      .get("/classifieds/seo/route", {
        params: { path: pathname, query: search.replace(/^\?/, "") },
      })
      .then(({ data }) => {
        if (!alive) return null;
        if (data?.status === 301 && data.location && !data.location.startsWith("http")) {
          go(data.location);
          return null;
        }
        return sharedApi.get("/legacy-redirect", { params: { path: pathname } });
      })
      .catch(() => sharedApi.get("/legacy-redirect", { params: { path: pathname } }))
      .then((resp) => {
        if (!alive || !resp) return;
        const data = resp.data;
        // Iter 707 — absolute targets (e.g. the admin panel on another
        // host) must leave the SPA entirely; router.replace would 404.
        if (typeof data?.target === "string" && data.target.startsWith("http")) {
          window.location.replace(data.target);
          return;
        }
        // WO 800 fix — the backend answers unmapped slugs with a generic
        // fallback target "/" (confidence "fallback"); that must not beat
        // the /c/{slug} hop for /category/{slug} URLs.
        const generic = data?.confidence === "fallback" || data?.target === "/";
        go(!generic && data?.target ? mapLegacyTarget(data.target) + search : fallbackPath());
      })
      .catch(() => {
        go(fallbackPath());
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
